import { useEffect, useState } from 'react'
import { Api } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import { env } from '../config/env.js'
import EditableField from '../components/review/EditableField.jsx'

const PLAN_LABELS = { decouverte: 'Découverte', cabinet: 'Cabinet', cabinet_pro: 'Cabinet Pro' }

export default function SettingsPage() {
  const { token, onUnauthorized, usage, logout } = useAuth()
  const [tenant, setTenant] = useState(null)
  const [err, setErr] = useState(null)

  useEffect(() => {
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    api.getTenant().then(setTenant).catch((e) => setErr(e.message))
  }, [token, onUnauthorized])

  const patchField = async (partial) => {
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    const updated = await api.patchTenant(partial)
    setTenant(updated)
  }

  const advisor = usage?.advisor
  const plan = usage?.plan
  const planLabel = PLAN_LABELS[plan] || 'Plan inconnu'
  const ratio = usage && usage.monthly_sessions_quota
    ? usage.monthly_session_count / usage.monthly_sessions_quota
    : 0

  return (
    <div className="max-w-[760px] space-y-6 px-7 py-8 md:px-12 lg:px-20">
      <h1 className="text-[22px] font-semibold tracking-[-0.02em]">Réglages</h1>

      {err && <div className="rounded-lg border border-terra-500/40 bg-terra-50 px-4 py-3 text-[12.5px] text-terra-600">{err}</div>}

      <section className="rounded-xl border border-line bg-white p-5">
        <h2 className="mb-3 text-[14px] font-medium">Conseiller</h2>
        {advisor ? (
          <div className="space-y-1">
            <div className="text-[14px] font-semibold">{advisor.name}</div>
            <div className="text-[12.5px] text-muted">{advisor.email} · {advisor.role}</div>
            <p className="mt-3 text-[11.5px] text-muted">Pour modifier ces informations, contactez votre administrateur.</p>
          </div>
        ) : (
          <p className="text-[12.5px] text-muted">Identité du conseiller indisponible (mode démo).</p>
        )}
      </section>

      <section className="rounded-xl border border-line bg-white p-5">
        <h2 className="mb-3 text-[14px] font-medium">Cabinet</h2>
        {!tenant && <p className="text-[12.5px] text-muted">Chargement…</p>}
        {tenant && (
          <div className="space-y-3">
            <EditableField label="Nom" value={tenant.Name || ''} onCommit={(v) => patchField({ name: v })} />
            <EditableField label="Adresse" value={tenant.Address || ''} onCommit={(v) => patchField({ address: v })} />
            <EditableField label="Téléphone" value={tenant.Phone || ''} onCommit={(v) => patchField({ phone: v })} />
            <EditableField label="SIREN/SIRET" value={tenant.RegistrationNum || ''} onCommit={(v) => patchField({ registration_num: v })} />
          </div>
        )}
      </section>

      <section className="rounded-xl border border-line bg-white p-5">
        <h2 className="mb-3 text-[14px] font-medium">Plan</h2>
        <div className="text-[14px] font-semibold">{planLabel}</div>
        {usage && (
          <>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-bg">
              <div
                className={
                  'h-full transition-all ' +
                  (ratio >= 1 ? 'bg-terra-500' : ratio >= 0.8 ? 'bg-amber-500' : 'bg-line')
                }
                style={{ width: `${Math.min(100, ratio * 100)}%` }}
              />
            </div>
            <div className="mt-1.5 text-[11.5px] text-muted">
              {usage.monthly_session_count}/{usage.monthly_sessions_quota} séances ce mois · réinitialise le 1er du mois prochain
            </div>
          </>
        )}
        <ul className="mt-5 space-y-2">
          {['decouverte', 'cabinet', 'cabinet_pro'].map((p) => {
            const isCurrent = p === plan
            return (
              <li
                key={p}
                className={`flex items-center justify-between rounded-lg border px-4 py-3 ${
                  isCurrent ? 'border-terra-500 bg-terra-50' : 'border-line bg-bg'
                }`}
              >
                <span className="text-[13.5px]">{PLAN_LABELS[p]}</span>
                <span className="text-[12px] text-muted">
                  {p === 'decouverte' && '10 séances/mois · 0 j rétention'}
                  {p === 'cabinet' && '30 séances/mois · 90 j rétention'}
                  {p === 'cabinet_pro' && '100 séances · 365 j rétention'}
                </span>
              </li>
            )
          })}
        </ul>
        <p className="mt-3 text-[11.5px] text-muted">Pour changer de plan, contactez-nous pour un devis.</p>
      </section>

      <section>
        <button
          onClick={logout}
          className="rounded-lg border border-terra-500/40 bg-white px-4 py-2 text-[13px] text-terra-600 hover:bg-terra-50"
        >
          Se déconnecter
        </button>
      </section>
    </div>
  )
}
